import { useLingui } from '@lingui/react/macro';
import type { FC, ReactNode } from 'react';
import type { WealthCoin } from '@/backend';
import { useCoin } from '@/shared/api';
import { Coin } from '@/shared/Coin';
import { PriceInput } from '@/shared/PriceInput';
import { cn } from '@/shared/react';
import { SearchableSelect } from '@/shared/SearchableSelect';

export const CoinPriceInput: FC<{
  label?: string;
  coins?: WealthCoin[];
  price?: string;
  onPriceChange?: (newValue?: string) => void;
  coin: string;
  onCoinChange?: (newCoin: string) => void;
  className?: string;
  loading?: boolean;
  footer?: ReactNode;
}> = ({
  label,
  coins,
  price = '0',
  onPriceChange,
  coin,
  onCoinChange,
  loading,
  className,
  footer,
}) => {
  const { t } = useLingui();
  const coinObject = useCoin(coin);

  return (
    <div
      className={cn(
        'border border-border-disabled rounded-lg max-w-full overflow-hidden',
        className,
      )}
    >
      <div className={cn(loading && 'shimmer')}>
        <label className="w-full text-sm text-content-tertiary mb-1 block text-start px-3 pt-3">
          {label}
        </label>
        <PriceInput
          size={16}
          value={price}
          onChange={onPriceChange}
          precision={coinObject.data?.precision}
          className="!border-transparent !bg-transparent"
          required
          placeholder={t`Amount`}
          disabled={loading}
          suffix={
            <SearchableSelect
              size={10}
              value={coin}
              onChange={(n: string | undefined) => {
                if (n) onCoinChange?.(n);
              }}
              placeholder={label}
              searchPlaceholder={t`Search coins...`}
              className="min-w-20 !rounded-full !bg-transparent"
              required
              disabled={loading || (coins?.length ?? 0) < 2}
              options={
                coins?.map((item) => ({
                  key: item.slug,
                  value: item.slug,
                  searchText:
                    `${item.symbol} ${item.name} ${item.slug} ${item.iraqi_name || ''}`.toLowerCase(),
                  label: () => <Coin className="me-2" coin={item} size={6} />,
                })) ?? []
              }
            />
          }
        />
      </div>
      {footer && (
        <>
          <div className="h-px bg-border-disabled mx-3" />
          <div className="text-xs h-12">{footer}</div>
        </>
      )}
    </div>
  );
};
